// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 201d3dd3-50be-4c3a-ab85-9e2382488e62

// Primordia auth — Cognito via AWS SDK v3 directly (no Amplify).
// Adapted from the aws-transfer adminportal pattern, with one deliberate fix:
// the session gate validates the REAL JWT expiry instead of a sticky flag.

import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  AssociateSoftwareTokenCommand,
  VerifySoftwareTokenCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  ChangePasswordCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { getConfig } from './config';
import { createSsoClient } from './ssoClient';

const KEY = 'primordia.session';
// PKCE material for an in-flight hosted-UI round trip. sessionStorage only, and
// cleared on every exit path (success, failure, sign-out) — a verifier that
// outlives its exchange is a replayable secret.
const PKCE_KEY = 'primordia.pkce';

const client = () => new CognitoIdentityProviderClient({ region: getConfig().region || 'us-east-1' });

// This pool signs in by email with UsernameConfiguration unset, and Cognito's
// default in that case is CASE SENSITIVE. So an account stored as
// `MKatsanes@optimumhit.com` rejected the lowercase its owner naturally typed,
// with "Incorrect username or password" — indistinguishable from a wrong
// password, which is why it cost a real person a real afternoon.
//
// UsernameConfiguration can only be set when the pool is CREATED, so this
// cannot be fixed on the pool without replacing it and every user in it.
// Instead every address is folded to lowercase on the way in, here and at
// user creation in the API, so both sides of the comparison always agree.
// Every stored address was normalised to match.
const normalizeEmail = (e) => String(e || '').trim().toLowerCase();

// Decode a JWT payload (no verification — the API verifies; this is for display
// and expiry checks only).
export function decodeJwt(token) {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(escape(atob(part))));
  } catch (e) {
    return null;
  }
}

function saveSession(authResult, extra = {}) {
  const idToken = authResult.IdToken;
  const claims = decodeJwt(idToken) || {};
  const session = {
    idToken,
    accessToken: authResult.AccessToken,
    refreshToken: authResult.RefreshToken,
    exp: claims.exp ? claims.exp * 1000 : Date.now() + 3600_000,
    email: claims.email || claims['cognito:username'],
    tenantId: claims['custom:companyId'] || null,
    tenantName: claims['custom:companyName'] || null,
    groups: claims['cognito:groups'] || [],
    // Federated sessions must also be torn down at the hosted UI on sign-out.
    sso: !!extra.sso,
  };
  sessionStorage.setItem(KEY, JSON.stringify(session));
  return session;
}

export function getSession() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    // THE FIX: expired token == not authenticated, no sticky flag.
    if (!s.exp || Date.now() >= s.exp) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return s;
  } catch (e) {
    return null;
  }
}

export const isAuthed = () => !!getSession();
export const getIdToken = () => (getSession() || {}).idToken || null;
export const isMaster = () => {
  const s = getSession();
  return !!s && (s.groups || []).includes('OptimumAdmins');
};
// A user with a valid token but no tenant and no group is authenticated and
// entitled to nothing. That is the expected landing state for a federated user
// until an admin assigns them — never paper over it with a default tenant.
export function needsWorkspaceAssignment(session) {
  const s = session || getSession();
  return !!s && !s.tenantId && !(s.groups || []).length;
}

export function signOut() {
  // Read before clearing: we need to know whether this was a federated session.
  let wasSso = false;
  try {
    const raw = sessionStorage.getItem(KEY);
    wasSso = !!raw && !!JSON.parse(raw).sso;
  } catch (e) {
    wasSso = false;
  }
  sessionStorage.removeItem(KEY);
  sessionStorage.removeItem(PKCE_KEY);

  // Clearing local storage alone leaves the hosted-UI cookie in place, so the
  // next sign-in attempt bounces straight back through the IdP and the user
  // appears to have never signed out. Tear the hosted session down too.
  const url = sso.logoutUrl();
  if (wasSso && url) window.location.assign(url);
}

// ---------------------------------------------------------------------------
// Challenge chain.
//
// Cognito drives sign-in as a chain, not a single step, and EVERY response to a
// challenge returns a fresh Session that invalidates the one before it. So each
// call below hands the session it just received to the next call. Replaying a
// stale session comes back as NotAuthorizedException, which presents as "wrong
// password" even though the password was correct — hence the single funnel:
// whatever Cognito returns is resolved in one place, so a challenge arriving
// second (MFA_SETUP straight after NEW_PASSWORD_REQUIRED, which is exactly what
// a brand-new user hits) is handled identically to one arriving first.
//
// Hosted-UI (SSO) sign-in never reaches any of this: Entra asserts the user's
// MFA and Cognito hands back tokens at the code exchange.
// ---------------------------------------------------------------------------

function unsupportedChallenge(name) {
  const e = new Error(`This account requires a sign-in step this app cannot complete (${name}). `
    + 'Please contact an administrator.');
  e.name = 'UnsupportedChallengeException';
  return e;
}

// Cognito wants the pool's canonical username in ChallengeResponses, which in
// an alias pool is not necessarily what the user typed. It is handed back on
// the challenge itself, so prefer that and fall back to the typed address.
function challengeUsername(res, email) {
  const p = res.ChallengeParameters || {};
  return p.USER_ID_FOR_SRP || p.USERNAME || email;
}

// Resolve anything Cognito returns: tokens, or the next challenge to present.
async function resolveAuth(res, email, failMessage) {
  if (res.AuthenticationResult) return { ok: true, session: saveSession(res.AuthenticationResult) };

  const base = { session: res.Session, email, username: challengeUsername(res, email) };
  switch (res.ChallengeName) {
    case 'NEW_PASSWORD_REQUIRED':
      return { challenge: 'NEW_PASSWORD_REQUIRED', ...base };
    case 'SOFTWARE_TOKEN_MFA':
      return { challenge: 'SOFTWARE_TOKEN_MFA', ...base };
    case 'MFA_SETUP':
      return beginMfaSetup(res, email);
    case 'SELECT_MFA_TYPE': {
      // TOTP is the only factor this pool offers (SMS is deliberately off), so
      // answer for the user rather than make them pick from a list of one.
      const next = await client().send(new RespondToAuthChallengeCommand({
        ChallengeName: 'SELECT_MFA_TYPE',
        ClientId: getConfig().userPoolClientId,
        Session: res.Session,
        ChallengeResponses: { USERNAME: base.username, ANSWER: 'SOFTWARE_TOKEN_MFA' },
      }));
      // One hop only — never loop on a pool that keeps asking.
      if (next.ChallengeName === 'SELECT_MFA_TYPE') throw unsupportedChallenge(next.ChallengeName);
      return resolveAuth(next, email, failMessage);
    }
    default:
      if (res.ChallengeName) throw unsupportedChallenge(res.ChallengeName);
      throw new Error(failMessage || 'Sign-in failed');
  }
}

// Returns { ok } on success, or a { challenge, session, ... } descriptor when
// Cognito wants another step: NEW_PASSWORD_REQUIRED (temp-password flow),
// MFA_SETUP (first sign-in once MFA is required), or SOFTWARE_TOKEN_MFA.
export async function login(email, password) {
  const cfg = getConfig();
  const user = normalizeEmail(email);
  const res = await client().send(new InitiateAuthCommand({
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: cfg.userPoolClientId,
    AuthParameters: { USERNAME: user, PASSWORD: password },
  }));
  return resolveAuth(res, user, 'Login failed');
}

export async function completeNewPassword(email, newPassword, cognitoSession, username) {
  const cfg = getConfig();
  const res = await client().send(new RespondToAuthChallengeCommand({
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: cfg.userPoolClientId,
    Session: cognitoSession,
    ChallengeResponses: { USERNAME: username || email, NEW_PASSWORD: newPassword },
  }));
  // Under an MFA-required pool this is where MFA_SETUP shows up, carried on the
  // session THIS response returned — never on the one we just spent above.
  return resolveAuth(res, email, 'Could not set password');
}

// ---------------------------------------------------------------------------
// TOTP multi-factor authentication.
// ---------------------------------------------------------------------------

const MFA_ISSUER = 'Primordia';

// Standard otpauth:// URI, understood by every authenticator app. The issuer
// prefix on the label keeps the entry from showing up as a bare email address
// in a user's app. This string embeds the shared secret: it is handed to the
// UI to draw a QR code and is never logged, stored, or put in a link.
function otpauthUri(email, secret) {
  return `otpauth://totp/${MFA_ISSUER}:${encodeURIComponent(email)}`
    + `?secret=${encodeURIComponent(secret)}&issuer=${MFA_ISSUER}`;
}

// MFA_SETUP challenge: the challenge itself carries no secret, only permission
// to associate one. AssociateSoftwareToken mints it AND rotates the session, so
// the rotated session is what the caller must carry into verification.
async function beginMfaSetup(res, email) {
  const assoc = await client().send(new AssociateSoftwareTokenCommand({ Session: res.Session }));
  return {
    challenge: 'MFA_SETUP',
    session: assoc.Session,
    email,
    username: challengeUsername(res, email),
    secretCode: assoc.SecretCode,
    otpauthUri: otpauthUri(email, assoc.SecretCode),
  };
}

// Finish first-time enrolment: prove the user's app is generating the right
// codes, then answer the original MFA_SETUP challenge to complete sign-in.
// `setup` is the descriptor returned by beginMfaSetup.
export async function completeMfaSetup(code, setup) {
  const cfg = getConfig();
  const verify = await client().send(new VerifySoftwareTokenCommand({
    Session: setup.session,
    UserCode: code,
    FriendlyDeviceName: 'Primordia',
  }));
  if (verify.Status !== 'SUCCESS') {
    const e = new Error('That code was not accepted.');
    e.name = 'CodeMismatchException';
    throw e;
  }
  const res = await client().send(new RespondToAuthChallengeCommand({
    ChallengeName: 'MFA_SETUP',
    ClientId: cfg.userPoolClientId,
    // Rotated a second time by VerifySoftwareToken — setup.session is spent.
    Session: verify.Session,
    ChallengeResponses: { USERNAME: setup.username || setup.email },
  }));
  return resolveAuth(res, setup.email, 'Could not finish authenticator setup');
}

// SOFTWARE_TOKEN_MFA challenge — every sign-in after enrolment. `mfa` is the
// descriptor returned by login().
export async function completeMfa(code, mfa) {
  const res = await client().send(new RespondToAuthChallengeCommand({
    ChallengeName: 'SOFTWARE_TOKEN_MFA',
    ClientId: getConfig().userPoolClientId,
    Session: mfa.session,
    ChallengeResponses: {
      USERNAME: mfa.username || mfa.email,
      SOFTWARE_TOKEN_MFA_CODE: code,
    },
  }));
  return resolveAuth(res, mfa.email, 'Could not verify that code');
}

// Recovery goes through OUR API, not straight to Cognito, because Cognito's
// ForgotPassword refuses an account that has never completed its first sign-in
// (FORCE_CHANGE_PASSWORD) — the invited-but-lost-the-email dead end. The
// endpoint branches on the account state server-side: fresh invitation (new
// temporary password) for a never-signed-in account, the ordinary code email
// for everyone else. It answers {ok:true} for every outcome, same as this form
// reports — the enumeration silence is preserved end to end.
//
// Falls back to the direct Cognito call if the API is unreachable (an older
// deployed API without the route, or local dev without the server), so the
// worst case is exactly the behaviour this replaces.
export async function forgotPassword(email) {
  const apiUrl = (getConfig().apiUrl || '').replace(/\/+$/, '');
  if (apiUrl) {
    try {
      const res = await fetch(`${apiUrl}/auth/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizeEmail(email) }),
      });
      if (res.ok) return;
    } catch (e) {
      /* fall through to the direct call */
    }
  }
  await client().send(new ForgotPasswordCommand({
    ClientId: getConfig().userPoolClientId, Username: normalizeEmail(email),
  }));
}

export async function confirmForgotPassword(email, code, newPassword) {
  await client().send(new ConfirmForgotPasswordCommand({
    ClientId: getConfig().userPoolClientId,
    Username: normalizeEmail(email),
    ConfirmationCode: code,
    Password: newPassword,
  }));
}

// A signed-in user changing their OWN password. Authorised by the access token
// rather than by an admin call, so it works for every role and needs no
// server-side privilege: the token IS the proof of who is asking, which is why
// the current password must still be supplied — a stolen tab should not be
// enough to lock the real owner out.
export async function changeOwnPassword(previousPassword, proposedPassword) {
  const s = getSession();
  if (!s || !s.accessToken) {
    const e = new Error('Your session has expired. Please sign in again.');
    e.name = 'NotAuthorizedException';
    throw e;
  }
  await client().send(new ChangePasswordCommand({
    AccessToken: s.accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  }));
}

// ---------------------------------------------------------------------------
// Hosted UI single sign-on — authorization code + PKCE.
//
// The app client is a PUBLIC client with no secret, so the code grant is only
// safe with PKCE: the verifier never leaves this browser, and an intercepted
// code is useless without it. The implicit grant is disabled on the client on
// purpose — it would put tokens in the URL fragment, where they land in history
// and in referrers.
//
// This runs ALONGSIDE the password flow above. Local sign-in stays as the
// deliberate break-glass path for when the directory is unreachable.
// ---------------------------------------------------------------------------

// The mechanics live in ssoClient.js — a deliberately framework-free,
// app-agnostic module extracted so Scout can carry the identical file (it
// fixes a bug each app had a different half of: this app stored the CSRF
// state but never SENT it; Scout sent no state at all). This file only adds
// what is Primordia's business: where config comes from, and what a session
// is. Config is read lazily inside the client on every call, because
// config.json loads after boot.
const sso = createSsoClient({
  getConfig,
  storageKey: PKCE_KEY,
  legacyProviderLabel: 'Sign in with Optimum',
});

// A hosted-UI domain existing is NOT the same as an identity provider being
// attached to it. The domain is deployed and the whole PKCE flow is wired, but
// until a directory is actually federated there is nothing on the other end:
// pressing the button would bounce off Cognito with an unhelpful error.
//
// So a button requires a provider in runtime config, written by the deploy
// script from what AuthStack actually attached to the pool. The plumbing can
// therefore sit finished and dormant without offering anyone a control that
// cannot work. Attach a provider, redeploy, and its button appears on its own.
//
// `ssoProviders` (a list) is the current shape; `ssoProvider` (one name) is the
// original single-Entra config and is still honoured, so a stage whose
// config.json predates the list keeps working.
export const ssoProviders = () => sso.providers();

// The providers that get a button on the shared login page.
//
// THIS IS A TENANT-ISOLATION DECISION, NOT A COSMETIC ONE. One login page serves
// every client, so a button per federated client publishes Optimum's customer
// list to all of them — the disclosure rule 1 exists to prevent, arriving
// through the front door instead of the database. A client's provider is
// reached by the /?idp=<name> deep link handed to that client alone.
export const visibleSsoProviders = () => sso.visibleProviders();

export const ssoAvailable = () => sso.available();

// The provider name to send to Cognito, e.g. 'EntraOptimum'. Configured, never
// guessed: the first VISIBLE provider, so a bare beginSso() cannot silently send
// a user to a client's directory.
export const ssoProviderName = () => sso.defaultProviderName();

// `?idp=<name>` on the login URL, which is how a client reaches a provider that
// has no button. Matched against the configured list rather than trusted: the
// value comes out of the address bar, and forwarding an arbitrary string to
// Cognito's identity_provider parameter is a redirect this app should not offer.
// Case-insensitive, because these get typed and pasted by hand.
export const requestedSsoProvider = () => sso.requestedProvider();

// True when the current URL is a hosted-UI callback, so the UI can show the
// exchange spinner on first paint instead of flashing the password form.
export const hasSsoRedirect = () => sso.hasRedirect();

// Redirect to the hosted UI for `provider` (the Cognito IdP name). Never
// resolves on success — the browser navigates away.
export const beginSso = (provider) => sso.begin(provider);

// Complete a redirect back from the hosted UI. The client owns the mechanics
// (state check, stale-vs-mismatch, token exchange, cleanup); what happens on
// success is Primordia's decision alone — the raw tokens go through the SAME
// persistence path as the password flow, so nothing downstream can tell a
// federated session from a local one.
async function runSsoRedirect() {
  const out = await sso.complete();
  if (out.status !== 'ok') return out;
  const session = saveSession({
    IdToken: out.tokens.id_token,
    AccessToken: out.tokens.access_token,
    RefreshToken: out.tokens.refresh_token,
  }, { sso: true });
  return {
    status: 'signed-in',
    session,
    needsAssignment: needsWorkspaceAssignment(session),
  };
}

// Memoised so a double mount (React StrictMode) cannot try to redeem the same
// one-time code twice.
let ssoRedirect = null;
export function completeSsoRedirect() {
  if (!ssoRedirect) ssoRedirect = runSsoRedirect();
  return ssoRedirect;
}
